import { CloudOff, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OfflineOrder } from '@/lib/offline/offlineStore'

/**
 * The held order the cart is currently adding to (F-FE-N31).
 *
 * This is the offline twin of SentItemsSection, and deliberately NOT that
 * component: SentItemsSection takes an `Order`, and a held order has no server
 * row, no id, no totals and no payments. Faking one so the rail would accept it
 * would put an invented total on screen and let it leak into the pricing and
 * payment paths — the exact class of mistake the "never show assumed numbers"
 * rule exists to stop.
 *
 * So it shows only what this device actually knows: the number already quoted
 * to the customer, and the lines already rung. No money. The bill is computed
 * server-side when the order replays.
 */
export function OfflineHeldTicket({
  order,
  nameFor,
  onClear,
}: {
  order: OfflineOrder
  /** Resolves a product id to its menu name; falls back to the id if unknown. */
  nameFor: (productId: string) => string
  onClear: () => void
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <CloudOff className="h-4 w-4 shrink-0" />
          <span className="truncate">
            Adding to <span className="font-mono">#{order.orderNumber}</span>
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 px-2 text-amber-900 hover:bg-amber-200/50 dark:text-amber-200"
          onClick={onClear}
        >
          <X className="mr-1 h-3.5 w-3.5" />
          Done
        </Button>
      </div>

      <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
        Held on this device — rung at{' '}
        {new Date(order.capturedAt).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
        . New items join this sale when it syncs.
      </p>

      <ul className="mt-2 space-y-0.5 text-sm">
        {order.lines.map((l, i) => (
          // Index-keyed on purpose: lines are append-only and never reordered
          // or removed, so position is stable, and two rounds of the same
          // product are two distinct rows that must both render.
          <li key={`${l.productId}-${i}`} className="flex items-baseline gap-2">
            <span className="w-6 shrink-0 text-right font-medium tabular-nums text-muted-foreground">
              {l.quantity}×
            </span>
            <span className="min-w-0">
              <span className="truncate">{nameFor(l.productId)}</span>
              {l.special_instructions ? (
                <span className="block text-xs italic text-muted-foreground">
                  {l.special_instructions}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
